import { gql, useMutation } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import { DatePicker } from '@orn/shared-web/components/DatePicker';
import Form from '@orn/shared-web/components/Form';
import Input from '@orn/shared-web/components/Input';
import Modal from '@orn/shared-web/components/Modal';
import Select from '@orn/shared-web/components/Select';
import TextArea from '@orn/shared-web/components/Textarea';
import * as React from 'react';

import { BillSubmissionSource, BillSubmissionStatus } from '../../../__generated__/globalTypes';
import logger from '../../../lib/logger';
import { dollarsToCents } from '../../../utils/currency';
import AdminSelectMerchant from '../AdminSelectMerchant';
import AdminSelectRecurringTransaction from '../AdminSelectRecurringTransaction';
import AdminSelectUser from '../AdminSelectUser';

import { autoPayOptions, getDatePickerValue } from './BillFormShared';

const ADMIN_CREATE_BILL_SUBMISSION = gql`
  mutation AdminCreateBillSubmission($input: AdminCreateBillSubmissionInput!) {
    adminCreateBillSubmission(input: $input) {
      id
    }
  }
`;

interface IAdminCreateBillSubmissionMutation {
  adminCreateBillSubmission: {
    id: string;
  };
}

interface IAdminCreateBillSubmissionInput {
  userId: string;
  merchantId?: string;
  recurringTransactionId?: string;
  status: BillSubmissionStatus;
  source: BillSubmissionSource;
  providerName?: string;
  amountDue?: number;
  dueDate?: string;
  description?: string;
  accountNumber?: string;
  invoiceNumber?: string;
  minAmountDue?: number;
  statementDate?: string;
  serviceStartDate?: string;
  serviceEndDate?: string;
  isAutopay?: boolean;
  paymentUrl?: string;
  lateFeeAmount?: number;
  lateFeeDate?: string;
  providerPhone?: string;
  providerEmail?: string;
  payerName?: string;
  previousAccountBalance?: number;
}

interface IAdminCreateBillSubmissionMutationVariables {
  input: IAdminCreateBillSubmissionInput;
}

interface ICreateBillSubmissionModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const billSubmissionStatusOptions: Array<{ id: BillSubmissionStatus; label: string }> = [
  { id: BillSubmissionStatus.NeedsReview, label: 'Needs review' },
  { id: BillSubmissionStatus.Rejected, label: 'Rejected' },
  { id: BillSubmissionStatus.Converted, label: 'Converted' },
];

const billSubmissionSourceOptions: Array<{ id: BillSubmissionSource; label: string }> = [
  { id: BillSubmissionSource.Email, label: 'Email' },
  { id: BillSubmissionSource.Sms, label: 'SMS' },
];

const CreateBillSubmissionModal = ({ isOpen, onOpenChange }: ICreateBillSubmissionModalProps) => {
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const [merchantId, setMerchantId] = React.useState('');
  const [recurringTransactionId, setRecurringTransactionId] = React.useState('');
  const [status, setStatus] = React.useState<BillSubmissionStatus>(BillSubmissionStatus.NeedsReview);
  const [source, setSource] = React.useState<BillSubmissionSource>(BillSubmissionSource.Email);
  const [providerName, setProviderName] = React.useState('');
  const [amountDue, setAmountDue] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [accountNumber, setAccountNumber] = React.useState('');
  const [invoiceNumber, setInvoiceNumber] = React.useState('');
  const [minAmountDue, setMinAmountDue] = React.useState('');
  const [statementDate, setStatementDate] = React.useState('');
  const [serviceStartDate, setServiceStartDate] = React.useState('');
  const [serviceEndDate, setServiceEndDate] = React.useState('');
  const [isAutopay, setIsAutopay] = React.useState<'unset' | 'true' | 'false'>('unset');
  const [paymentUrl, setPaymentUrl] = React.useState('');
  const [lateFeeAmount, setLateFeeAmount] = React.useState('');
  const [lateFeeDate, setLateFeeDate] = React.useState('');
  const [providerPhone, setProviderPhone] = React.useState('');
  const [providerEmail, setProviderEmail] = React.useState('');
  const [payerName, setPayerName] = React.useState('');
  const [previousAccountBalance, setPreviousAccountBalance] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const [createBillSubmission, { loading }] = useMutation<
    IAdminCreateBillSubmissionMutation,
    IAdminCreateBillSubmissionMutationVariables
  >(ADMIN_CREATE_BILL_SUBMISSION, {
    refetchQueries: selectedUserId ? ['AdminListBillSubmissions'] : [],
    awaitRefetchQueries: true,
  });

  const resetForm = React.useCallback(() => {
    setSelectedUserId(null);
    setMerchantId('');
    setRecurringTransactionId('');
    setStatus(BillSubmissionStatus.NeedsReview);
    setSource(BillSubmissionSource.Email);
    setProviderName('');
    setAmountDue('');
    setDueDate('');
    setDescription('');
    setAccountNumber('');
    setInvoiceNumber('');
    setMinAmountDue('');
    setStatementDate('');
    setServiceStartDate('');
    setServiceEndDate('');
    setIsAutopay('unset');
    setPaymentUrl('');
    setLateFeeAmount('');
    setLateFeeDate('');
    setProviderPhone('');
    setProviderEmail('');
    setPayerName('');
    setPreviousAccountBalance('');
    setError(null);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedUserId) {
      setError('Please select a user.');
      return;
    }

    const parseOptionalAmountInCents = (value: string, label: string): number | null | undefined => {
      if (value.trim().length === 0) {
        return undefined;
      }
      const parsedValue = Number(value);
      if (Number.isNaN(parsedValue)) {
        setError(`${label} must be a valid number.`);
        return null;
      }
      return dollarsToCents(parsedValue);
    };

    const parsedAmountDue = parseOptionalAmountInCents(amountDue, 'Amount due');
    if (parsedAmountDue === null) {
      return;
    }
    const parsedMinAmountDue = parseOptionalAmountInCents(minAmountDue, 'Min amount due');
    if (parsedMinAmountDue === null) {
      return;
    }
    const parsedLateFeeAmount = parseOptionalAmountInCents(lateFeeAmount, 'Late fee amount');
    if (parsedLateFeeAmount === null) {
      return;
    }
    const parsedPreviousAccountBalance = parseOptionalAmountInCents(previousAccountBalance, 'Previous account balance');
    if (parsedPreviousAccountBalance === null) {
      return;
    }

    try {
      await createBillSubmission({
        variables: {
          input: {
            userId: selectedUserId,
            status,
            source,
            ...(merchantId.trim().length > 0 ? { merchantId: merchantId.trim() } : {}),
            ...(recurringTransactionId.trim().length > 0
              ? { recurringTransactionId: recurringTransactionId.trim() }
              : {}),
            ...(providerName.trim().length > 0 ? { providerName: providerName.trim() } : {}),
            ...(parsedAmountDue != null ? { amountDue: parsedAmountDue } : {}),
            ...(dueDate.trim().length > 0 ? { dueDate: dueDate.trim() } : {}),
            ...(description.trim().length > 0 ? { description: description.trim() } : {}),
            ...(accountNumber.trim().length > 0 ? { accountNumber: accountNumber.trim() } : {}),
            ...(invoiceNumber.trim().length > 0 ? { invoiceNumber: invoiceNumber.trim() } : {}),
            ...(parsedMinAmountDue != null ? { minAmountDue: parsedMinAmountDue } : {}),
            ...(statementDate.trim().length > 0 ? { statementDate: statementDate.trim() } : {}),
            ...(serviceStartDate.trim().length > 0 ? { serviceStartDate: serviceStartDate.trim() } : {}),
            ...(serviceEndDate.trim().length > 0 ? { serviceEndDate: serviceEndDate.trim() } : {}),
            ...(isAutopay !== 'unset' ? { isAutopay: isAutopay === 'true' } : {}),
            ...(paymentUrl.trim().length > 0 ? { paymentUrl: paymentUrl.trim() } : {}),
            ...(parsedLateFeeAmount != null ? { lateFeeAmount: parsedLateFeeAmount } : {}),
            ...(lateFeeDate.trim().length > 0 ? { lateFeeDate: lateFeeDate.trim() } : {}),
            ...(providerPhone.trim().length > 0 ? { providerPhone: providerPhone.trim() } : {}),
            ...(providerEmail.trim().length > 0 ? { providerEmail: providerEmail.trim() } : {}),
            ...(payerName.trim().length > 0 ? { payerName: payerName.trim() } : {}),
            ...(parsedPreviousAccountBalance != null ? { previousAccountBalance: parsedPreviousAccountBalance } : {}),
          },
        },
      });

      resetForm();
      onOpenChange(false);
    } catch (mutationError) {
      logger.error('Failed to create bill submission:', mutationError);
      setError('Failed to create bill submission. Please try again.');
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg" className="h-[80vh] max-h-[80vh]">
      <Modal.Header onClose={handleClose}>Create bill submission</Modal.Header>
      <Modal.Body className="min-h-0 flex-1 items-start px-0 pb-6 pt-0">
        <Form onSubmit={handleSubmit} className="flex h-full min-h-0 w-full flex-col">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="flex flex-col gap-4 px-6">
              <AdminSelectUser
                selectedUserId={selectedUserId}
                onSelectionChange={setSelectedUserId}
                className="w-full"
              />

              <AdminSelectMerchant
                selectedUserId={selectedUserId}
                selectedMerchantId={merchantId || null}
                onSelectionChange={(value, merchantName) => {
                  setMerchantId(value ?? '');
                  if (merchantName && merchantName.trim().length > 0) {
                    setProviderName(merchantName);
                  }
                }}
                className="w-full"
              />
              <AdminSelectRecurringTransaction
                selectedUserId={selectedUserId}
                selectedRecurringTransactionId={recurringTransactionId || null}
                onSelectionChange={(value) => setRecurringTransactionId(value ?? '')}
                className="w-full"
              />

              <Select
                label="Status"
                selectedKey={status}
                onSelectionChange={(key) => setStatus(key as BillSubmissionStatus)}
                items={billSubmissionStatusOptions}
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select>

              <Select
                label="Source"
                selectedKey={source}
                onSelectionChange={(key) => setSource(key as BillSubmissionSource)}
                items={billSubmissionSourceOptions}
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select>

              <Input
                label="Provider name (optional)"
                value={providerName}
                onChange={setProviderName}
                placeholder="Provider name"
              />
              <Input label="Amount due (optional)" value={amountDue} onChange={setAmountDue} placeholder="120.34" />
              <div className="flex w-full flex-col gap-1.5">
                <div className="text-secondary text-sm font-medium">Due date (optional)</div>
                <DatePicker
                  value={getDatePickerValue(dueDate)}
                  onChange={(dateValue) => setDueDate(dateValue ? dateValue.toString() : '')}
                />
              </div>
              <TextArea
                label="Description (optional)"
                value={description}
                onChange={setDescription}
                rows={3}
                placeholder="Bill description"
              />

              <Input
                label="Account number (optional)"
                value={accountNumber}
                onChange={setAccountNumber}
                placeholder="1234567890"
              />
              <Input
                label="Invoice number (optional)"
                value={invoiceNumber}
                onChange={setInvoiceNumber}
                placeholder="INV-1001"
              />
              <Input
                label="Min amount due (optional)"
                value={minAmountDue}
                onChange={setMinAmountDue}
                placeholder="45.67"
              />
              <div className="flex w-full flex-col gap-1.5">
                <div className="text-secondary text-sm font-medium">Statement date (optional)</div>
                <DatePicker
                  value={getDatePickerValue(statementDate)}
                  onChange={(dateValue) => setStatementDate(dateValue ? dateValue.toString() : '')}
                />
              </div>
              <div className="flex w-full flex-col gap-1.5">
                <div className="text-secondary text-sm font-medium">Service start date (optional)</div>
                <DatePicker
                  value={getDatePickerValue(serviceStartDate)}
                  onChange={(dateValue) => setServiceStartDate(dateValue ? dateValue.toString() : '')}
                />
              </div>
              <div className="flex w-full flex-col gap-1.5">
                <div className="text-secondary text-sm font-medium">Service end date (optional)</div>
                <DatePicker
                  value={getDatePickerValue(serviceEndDate)}
                  onChange={(dateValue) => setServiceEndDate(dateValue ? dateValue.toString() : '')}
                />
              </div>
              <Select
                label="Is autopay (optional)"
                selectedKey={isAutopay}
                onSelectionChange={(key) => setIsAutopay(key as 'unset' | 'true' | 'false')}
                items={autoPayOptions}
              >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
              </Select>
              <Input
                label="Payment URL (optional)"
                value={paymentUrl}
                onChange={setPaymentUrl}
                placeholder="https://payments.example.com"
              />
              <Input
                label="Late fee amount (optional)"
                value={lateFeeAmount}
                onChange={setLateFeeAmount}
                placeholder="12.00"
              />
              <div className="flex w-full flex-col gap-1.5">
                <div className="text-secondary text-sm font-medium">Late fee date (optional)</div>
                <DatePicker
                  value={getDatePickerValue(lateFeeDate)}
                  onChange={(dateValue) => setLateFeeDate(dateValue ? dateValue.toString() : '')}
                />
              </div>
              <Input
                label="Provider phone (optional)"
                value={providerPhone}
                onChange={setProviderPhone}
                placeholder="+1 555 555 5555"
              />
              <Input
                label="Provider email (optional)"
                value={providerEmail}
                onChange={setProviderEmail}
                placeholder="billing@provider.com"
              />
              <Input label="Payer name (optional)" value={payerName} onChange={setPayerName} placeholder="Jane Doe" />
              <Input
                label="Previous account balance (optional)"
                value={previousAccountBalance}
                onChange={setPreviousAccountBalance}
                placeholder="300.00"
              />
            </div>
          </div>

          <div className="border-primary mt-3 border-t px-6 pt-3">
            {error && <div className="text-error-primary mb-3 text-sm">{error}</div>}
            <div className="flex w-full gap-3">
              <Button color="secondary" onClick={handleClose} className="flex-1" isDisabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                className="flex-1"
                isDisabled={loading || !selectedUserId}
                isLoading={loading}
              >
                Create bill submission
              </Button>
            </div>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateBillSubmissionModal;
